import React, { useEffect, useMemo, useState } from 'react';
import { initSocket } from '../services/socketService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Siren, Building2, Loader2, Video, Users, User, X, CheckCircle2, AlertTriangle, PiggyBank, Phone } from 'lucide-react';
import { EmployerCrisisWizard } from './EmployerCrisisWizard';
import { Card, Button } from '../components';
import {
  fetchCrisisEmployerOverzicht,
  zegCrisisAbonnementOp,
  maakCrisisAlarm,
  sluitCrisisAlarm,
  euroCrisis,
  type CrisisAbonnement,
  type CrisisAlarm,
} from '../services/crisisApi';

const STATUS_LABEL: Record<CrisisAbonnement['status'], { tekst: string; kleur: string }> = {
  PENDING_MANDATE: { tekst: 'Wacht op SEPA-mandaat', kleur: 'bg-amber-100 text-amber-800' },
  ACTIVE: { tekst: 'Actief', kleur: 'bg-emerald-100 text-emerald-800' },
  PAST_DUE: { tekst: 'Incasso mislukt', kleur: 'bg-red-100 text-red-800' },
  CANCELED: { tekst: 'Opgezegd', kleur: 'bg-gray-100 text-gray-700' },
};

const ALARM_STATUS: Record<CrisisAlarm['status'], { tekst: string; kleur: string }> = {
  OPEN: { tekst: 'Alarm loopt — zoekt specialist', kleur: 'bg-red-100 text-red-800 animate-pulse' },
  ACCEPTED: { tekst: 'Specialist onderweg', kleur: 'bg-emerald-100 text-emerald-800' },
  CLOSED: { tekst: 'Afgerond', kleur: 'bg-gray-100 text-gray-700' },
  CANCELLED: { tekst: 'Geannuleerd', kleur: 'bg-gray-100 text-gray-500' },
};

const ESCALATIE_TEKST = ['eigen regio (0–60 s)', "buurregio's (61–120 s)", 'landelijk (vanaf 121 s)'];

/**
 * Tab 'Crisisdienst & Garantiefonds' in Mijn Belagom.
 * Stap 1: medewerkersaantal per vestiging → schil → SEPA-abonnement (Stripe).
 * Daarna: No-Claim teller per vestiging, de rode alarmknop (Single/Dual) en de alarmhistorie.
 */
export const EmployerCrisisPool: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [wizard, setWizard] = useState<{ open: boolean; locatieId: string | null }>({ open: false, locatieId: null });
  const [alarmModal, setAlarmModal] = useState<CrisisAbonnement | null>(null);
  const [alarmType, setAlarmType] = useState<'SINGLE' | 'DUAL'>('SINGLE');
  const [alarmOmschrijving, setAlarmOmschrijving] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['crisis', 'employer-overzicht'],
    queryFn: fetchCrisisEmployerOverzicht,
    // Lopend alarm: 10 s. Anders 30 s als vangnet — socket-events bereiken alleen de hoofdgebruiker, niet teamleden.
    refetchInterval: (q) => (q.state.data?.alarmen.some((a) => a.status === 'OPEN' || a.status === 'ACCEPTED') ? 10000 : 30000),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['crisis', 'employer-overzicht'] });

  // Realtime: medewerkersalarm of acceptatie door een specialist → direct verversen
  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => queryClient.invalidateQueries({ queryKey: ['crisis', 'employer-overzicht'] });
    const opNotificatie = (n: { type?: string }) => {
      if (n?.type === 'crisis_alarm') ververs();
    };
    socket.on('crisis_alarm_medewerker', ververs);
    socket.on('crisis_alarm_accepted', ververs);
    socket.on('new_notification', opNotificatie);
    return () => {
      socket.off('crisis_alarm_medewerker', ververs);
      socket.off('crisis_alarm_accepted', ververs);
      socket.off('new_notification', opNotificatie);
    };
  }, [queryClient]);

  const opzegMutation = useMutation({
    mutationFn: zegCrisisAbonnementOp,
    onSuccess: () => {
      toast.success('Abonnement opgezegd per einde lopende periode');
      invalidate();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Opzeggen mislukt'),
  });

  const alarmMutation = useMutation({
    mutationFn: ({ locationId, type, omschrijving }: { locationId: string; type: 'SINGLE' | 'DUAL'; omschrijving: string }) => maakCrisisAlarm(locationId, type, omschrijving),
    onSuccess: (alarm) => {
      const d = alarm.dekking;
      toast.success(
        d?.type === 'VOLLEDIG' || (!d && alarm.uit_tegoed)
          ? 'Alarm verzonden — gedekt door uw No-Claim tegoed (factuur € 0,-)'
          : d?.type === 'GEDEELTELIJK'
            ? `Alarm verzonden — ${d.gedekt} specialist gedekt door het Garantiefonds, ${d.gefactureerd} via de SEPA-verzamelfactuur`
            : 'Alarm verzonden — tegoed is op, uitruk gaat via de SEPA-verzamelfactuur',
        { duration: 8000 }
      );
      setAlarmModal(null);
      setAlarmOmschrijving('');
      invalidate();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Alarm versturen mislukt'),
  });

  const sluitMutation = useMutation({
    mutationFn: sluitCrisisAlarm,
    onSuccess: () => {
      toast.success('Alarm afgesloten');
      invalidate();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Afsluiten mislukt'),
  });

  const abonnementParam = searchParams.get('abonnement');

  const zonderAbonnement = useMemo(() => {
    if (!data) return [];
    const met = new Set(data.abonnementen.filter((a) => a.status !== 'PENDING_MANDATE').map((a) => a.location_id));
    return data.locaties.filter((l) => !met.has(l.id));
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Crisisdienst laden…
      </div>
    );
  }
  if (error || !data) {
    return <Card className="text-red-700">De crisisdienst kon niet worden geladen.</Card>;
  }

  const split = data.tarieven.split;

  return (
    <div className="space-y-6">
      {abonnementParam === 'gelukt' && (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <CheckCircle2 className="w-5 h-5 mt-0.5" />
          <div>SEPA-machtiging afgegeven. Zodra Stripe de eerste termijn bevestigt, staat het abonnement op Actief en is uw No-Claim tegoed beschikbaar.</div>
        </div>
      )}
      {abonnementParam === 'geannuleerd' && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div>De SEPA-machtiging is afgebroken. U kunt het abonnement opnieuw starten.</div>
        </div>
      )}

      {/* Intro + staffel */}
      <Card>
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-red-50 text-red-600">
            <Siren className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Landelijke Crisisdienst & Garantiefonds</h2>
            <p className="text-sm text-gray-600 mt-1">
              24/7 paraatheid van geaccrediteerde crisisspecialisten (GZ-psycholoog, bedrijfsmaatschappelijk werker, traumatoloog) in uw regio.
              Uw maandelijkse inleg wordt in code gesplitst: {Math.round(split.BELAGOM_FEE * 100)}% platform, {Math.round(split.POOL_SHARE * 100)}% stand-by pool en{' '}
              {Math.round(split.FONDS_SHARE * 100)}% Calamiteiten-Garantiefonds. Een uitruk binnen uw No-Claim tegoed kost u exact € 0,- extra.
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.tiers.map((t) => (
            <div key={t.id} className="rounded-lg border border-gray-200 p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Schil {t.schil_niveau}</div>
              <div className="font-semibold text-gray-900">{t.naam}</div>
              <div className="text-xs text-gray-500">
                {t.min_medewerkers} – {t.max_medewerkers >= 99999 ? '∞' : t.max_medewerkers} medewerkers
              </div>
              <div className="mt-2 text-xl font-bold text-[#0B1F3A]">
                {euroCrisis(t.maandelijkse_fee, 0)}
                <span className="text-xs font-normal text-gray-500"> / maand / vestiging</span>
              </div>
              <div className="mt-2 text-xs text-gray-700">
                <div>🛡️ {t.basis_vrije_uitrukken} vrije uitruk{t.basis_vrije_uitrukken > 1 ? 'ken' : ''} per jaar</div>
                <div>🚀 No-Claim cap: max. {t.max_no_claim_cap}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Acute noodhulp: {euroCrisis(data.tarieven.uurtarief, 0)} per uur, minimaal {data.tarieven.min_uren} uur per uitruk. Buiten het tegoed loopt de uitruk via de SEPA-verzamelfactuur.
          Ongebruikte vrije uitrukken rollen op 1 januari door tot de harde cap van uw schil.
        </p>
      </Card>

      {/* Actieve abonnementen + alarmknop */}
      {data.abonnementen.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.abonnementen.map((a) => {
            const st = STATUS_LABEL[a.status];
            const openAlarm = data.alarmen.find((al) => al.subscription_id === a.id && (al.status === 'OPEN' || al.status === 'ACCEPTED'));
            const tegoedTotaal = a.huidige_vrije_uitrukken + a.overgedragen_vrije_uitrukken;
            return (
              <Card key={a.id} className="flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="font-semibold text-gray-900">{a.vestiging_naam}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${st.kleur}`}>{st.tekst}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Schil {a.schil_niveau} · {a.schil_naam} · {a.aantal_medewerkers} medewerkers · regio {a.regio_code} · {euroCrisis(a.maandelijkse_fee, 0)}/maand
                    </div>
                  </div>
                  {a.status !== 'CANCELED' && a.status !== 'PENDING_MANDATE' && (
                    <button
                      type="button"
                      className="text-xs text-gray-400 hover:text-red-600"
                      onClick={() => {
                        if (window.confirm('Abonnement opzeggen per einde lopende periode?')) opzegMutation.mutate(a.id);
                      }}
                    >
                      Opzeggen
                    </button>
                  )}
                </div>

                {/* No-Claim teller */}
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-emerald-50 p-3">
                    <div className="text-2xl font-bold text-emerald-700">{a.beschikbare_uitrukken}</div>
                    <div className="text-[11px] text-emerald-800">vrije uitrukken beschikbaar</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-2xl font-bold text-gray-700">{tegoedTotaal}</div>
                    <div className="text-[11px] text-gray-600">
                      jaartegoed {a.kalenderjaar ?? ''} ({a.huidige_vrije_uitrukken} basis + {a.overgedragen_vrije_uitrukken} rollover)
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-2xl font-bold text-gray-700">{a.gebruikte_uitrukken}</div>
                    <div className="text-[11px] text-gray-600">gebruikt dit jaar · cap {a.max_no_claim_cap}</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1 text-[11px] text-gray-500">
                  <PiggyBank className="w-3.5 h-3.5" /> Ingelegd: {euroCrisis(a.totaal_ingelegd)} · waarvan Garantiefonds {euroCrisis(a.fonds_ingelegd)}
                </div>
                {a.contact_naam && (
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
                    <Phone className="w-3.5 h-3.5" /> Contact bij calamiteit: {a.contact_naam} ({a.contact_telefoon})
                    {a.sector && ` · ${data.sectoren.find((s) => s.code === a.sector)?.label ?? a.sector}`}
                    {a.ploegendienst ? ' · 24/7 ploegendienst' : ''}
                    {a.addendum_getekend_op && ` · addendum v${a.addendum_versie} getekend ${new Date(a.addendum_getekend_op).toLocaleDateString('nl-NL')}`}
                  </div>
                )}

                {/* Alarmknop */}
                <div className="mt-4">
                  {openAlarm ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${ALARM_STATUS[openAlarm.status].kleur}`}>{ALARM_STATUS[openAlarm.status].tekst}</span>
                        <span className="text-xs text-gray-500">cascade: {ESCALATIE_TEKST[openAlarm.escalatie_niveau] ?? 'landelijk'}</span>
                      </div>
                      {openAlarm.bron === 'MEDEWERKER' && (
                        <div className="mt-1 text-xs text-red-800">Afgegeven via het medewerkersportaal door {openAlarm.gemeld_door_pseudoniem}</div>
                      )}
                      {openAlarm.responses.length > 0 && (
                        <ul className="mt-2 text-xs text-gray-700 space-y-0.5">
                          {openAlarm.responses.map((r) => (
                            <li key={r.id}>
                              ✅ {r.volgnummer ? `#${r.volgnummer} ` : ''}{r.responder_naam} — geaccepteerd {new Date(r.geaccepteerd_op).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                              {r.gedekt_door && (
                                <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] ${r.gedekt_door === 'FONDS' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                  {r.gedekt_door === 'FONDS' ? 'Garantiefonds · € 0,-' : `verzamelfactuur · ${euroCrisis(data.tarieven.uurtarief, 0)}/u`}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {openAlarm.response_type === 'DUAL' && (
                        <div className="mt-2 text-xs text-gray-600">
                          {openAlarm.status === 'OPEN'
                            ? `First-Two-Accept: poort sluit zodra 2 specialisten hebben geaccepteerd (${openAlarm.responses.length}/2).`
                            : 'Duo compleet — beide specialisten zijn gekoppeld in een interne duo-chat en onderweg.'}
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {openAlarm.status === 'ACCEPTED' && openAlarm.video_url && (
                          <a href={openAlarm.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg bg-[#0B1F3A] px-3 py-1.5 text-xs font-medium text-white">
                            <Video className="w-3.5 h-3.5" /> Open BeLagom-videolink
                          </a>
                        )}
                        <Button size="sm" variant="outline" onClick={() => sluitMutation.mutate(openAlarm.id)} loading={sluitMutation.isPending}>
                          {openAlarm.status === 'OPEN' ? 'Alarm intrekken' : 'Alarm afsluiten'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={a.status === 'PENDING_MANDATE' || a.status === 'CANCELED'}
                      onClick={() => {
                        setAlarmType('SINGLE');
                        setAlarmModal(a);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-gray-300 px-4 py-3 text-white font-semibold shadow"
                    >
                      <Siren className="w-5 h-5" /> RODE ALARMKNOP — acute crisishulp
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* 🚨 Calamiteiten-onboarding: vestigingen zonder actief abonnement */}
      {zonderAbonnement.length > 0 && (
        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Siren className="w-5 h-5 text-red-600" /> Activeer 24/7 Landelijke Crisisdienst
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Drie korte stappen: wettelijke screening (sector, ploegendienst, Hoofd BHV), realtime tarief- & dekkingsbevestiging en de digitale SMS-verzegeling van het Addendum Crisisdienst. Daarna staat de rode alarmknop per direct live.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setWizard({ open: true, locatieId: zonderAbonnement[0]?.id ?? null })}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 px-5 py-3 text-white font-semibold shadow"
            >
              🚨 Start de activatie-wizard
            </button>
          </div>
          <div className="mt-4 divide-y divide-gray-100">
            {zonderAbonnement.map((l) => {
              const pending = data.abonnementen.find((a) => a.location_id === l.id && a.status === 'PENDING_MANDATE');
              return (
                <div key={l.id} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {l.naam} {l.is_hoofdvestiging && <span className="text-xs text-gray-500">(hoofdvestiging)</span>}
                    </div>
                    <div className="text-xs text-gray-500">
                      {l.postcode ?? 'geen postcode'} {l.plaats ?? ''} · {l.regio_code ? `regio ${l.regio_code}` : '⚠️ postcode ontbreekt — vul deze in bij Organisatieprofiel'}
                      {pending && (pending.addendum_getekend_op ? ' · addendum getekend, SEPA-machtiging nog niet afgerond' : ' · wizard nog niet afgerond')}
                    </div>
                  </div>
                  <Button size="sm" variant={pending ? 'primary' : 'outline'} disabled={!l.regio_code} onClick={() => setWizard({ open: true, locatieId: l.id })}>
                    {pending ? 'Activatie hervatten' : 'Activeren'}
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {data.locaties.length === 0 && (
        <Card className="text-sm text-gray-600">Maak eerst een vestiging aan onder Organisatieprofiel; het crisisabonnement loopt per vestiging.</Card>
      )}

      {/* Alarmhistorie */}
      {data.alarmen.length > 0 && (
        <Card>
          <h3 className="font-semibold text-gray-900">Alarmhistorie</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left py-2 pr-4">Moment</th>
                  <th className="text-left py-2 pr-4">Vestiging</th>
                  <th className="text-left py-2 pr-4">Type</th>
                  <th className="text-left py-2 pr-4">Status</th>
                  <th className="text-left py-2 pr-4">Dekking</th>
                  <th className="text-left py-2 pr-4">Specialisten / uren</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.alarmen.map((al) => (
                  <tr key={al.id}>
                    <td className="py-2 pr-4 whitespace-nowrap">{new Date(al.created_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="py-2 pr-4">
                      {al.vestiging_naam ?? '—'}
                      {al.bron === 'MEDEWERKER' && <div className="text-[11px] text-gray-500">via medewerkersportaal · {al.gemeld_door_pseudoniem}</div>}
                    </td>
                    <td className="py-2 pr-4">{al.response_type === 'DUAL' ? '👥 Dual' : '👤 Single'}</td>
                    <td className="py-2 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${ALARM_STATUS[al.status].kleur.replace(' animate-pulse', '')}`}>{ALARM_STATUS[al.status].tekst}</span>
                    </td>
                    <td className="py-2 pr-4">
                      {al.dekking?.type === 'GEDEELTELIJK'
                        ? `⚠️ Gedeeltelijk: ${al.dekking.gedekt}× Garantiefonds (−${al.punten_afgeboekt} tegoed) · ${al.dekking.gefactureerd}× verzamelfactuur`
                        : al.uit_tegoed || al.dekking?.type === 'VOLLEDIG'
                          ? `🛡️ Garantiefonds (−${al.punten_afgeboekt} tegoed) · factuur € 0,-`
                          : '🧾 Verzamelfactuur'}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      {al.responses.length === 0
                        ? '—'
                        : al.responses.map((r) => (
                            <div key={r.id}>
                              {r.volgnummer ? `#${r.volgnummer} ` : ''}{r.responder_naam}
                              {r.uren_gefactureerd != null && ` · ${r.uren_gefactureerd} u · ${euroCrisis(r.bedrag_bruto)}`}
                              {r.gedekt_door && <span className="text-gray-500"> · {r.gedekt_door === 'FONDS' ? 'fonds' : 'factuur'}</span>}
                            </div>
                          ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {wizard.open && (
        <EmployerCrisisWizard
          locaties={zonderAbonnement}
          tiers={data.tiers}
          sectoren={data.sectoren}
          startLocatieId={wizard.locatieId}
          onClose={() => setWizard({ open: false, locatieId: null })}
        />
      )}

      {/* Alarm-modal: Single / Dual */}
      {alarmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Siren className="w-5 h-5 text-red-600" /> Alarm — {alarmModal.vestiging_naam}
              </h3>
              <button type="button" onClick={() => setAlarmModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Selecteer de gewenste omvang van de crisis-inzet</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(
                  [
                    { type: 'SINGLE', icon: User, titel: '👤 Single-Response', kop: '1 specialist', aanbevolen: 'Individuele trauma’s, gerichte escalaties of kleinschalige incidenten.', kosten: 'Drukt 1 punt af van uw vrije uitruk-tegoed.' },
                    { type: 'DUAL', icon: Users, titel: '👥 Dual-Response', kop: '2 specialisten', aanbevolen: 'Ernstige incidenten met meerdere getuigen, complete afdelings-opvang of zware calamiteiten.', kosten: 'Drukt 2 punten af van uw vrije uitruk-tegoed.' },
                  ] as const
                ).map((o) => {
                  const Icon = o.icon;
                  const actief = alarmType === o.type;
                  return (
                    <button
                      key={o.type}
                      type="button"
                      onClick={() => setAlarmType(o.type)}
                      className={`rounded-lg border p-4 text-left ${actief ? 'border-red-500 bg-red-50 ring-1 ring-red-300' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <Icon className={`w-5 h-5 ${actief ? 'text-red-600' : 'text-gray-400'}`} />
                        {o.type === 'DUAL' && <span className="text-[10px] font-semibold text-amber-600">🌟 zware calamiteit</span>}
                      </div>
                      <div className="mt-2 font-medium text-gray-900">{o.titel}</div>
                      <div className="text-xs text-gray-700">{o.kop}</div>
                      <div className="mt-1 text-[11px] text-gray-500">Aanbevolen bij: {o.aanbevolen}</div>
                      <div className="mt-1 text-[11px] text-gray-500">Kosten: {o.kosten}</div>
                    </button>
                  );
                })}
              </div>
              {(() => {
                const gewenst = alarmType === 'DUAL' ? 2 : 1;
                const beschikbaar = Math.max(alarmModal.beschikbare_uitrukken, 0);
                const gedekt = Math.min(beschikbaar, gewenst);
                const gefactureerd = gewenst - gedekt;
                if (gefactureerd === 0) {
                  return (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
                      🛡️ Beschikbaar tegoed: <strong>{beschikbaar}</strong>. Deze {gewenst === 2 ? 'Dual-Response' : 'uitruk'} wordt volledig gedekt door het Garantiefonds — uw verzamelfactuur blijft op <strong>€ 0,-</strong> extra staan.
                    </div>
                  );
                }
                if (gedekt > 0) {
                  return (
                    <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
                        <div>
                          <strong>Let op — Fiscale Co-Payment:</strong> uw Garantiefonds dekt <strong>{gedekt} professional</strong> volledig (€ 0,-). De {gefactureerd === 1 ? 'tweede professional wordt' : `overige ${gefactureerd} professionals worden`} gefactureerd tegen het reguliere B2B Spoed-uurtarief van <strong>{euroCrisis(data.tarieven.uurtarief, 0)} per uur</strong> (min. {data.tarieven.min_uren} uur) via uw SEPA-verzamelfactuur.
                          <div className="mt-1 text-[11px] text-amber-800">De eerste specialist die accepteert valt onder het fonds, de tweede onder de factuur. Beschikbaar tegoed: {beschikbaar}.</div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
                      <div>
                        <strong>Tegoed is op (0 vrije uitrukken).</strong> {gewenst === 2 ? 'Beide professionals worden' : 'De uitruk wordt'} achteraf gefactureerd à {euroCrisis(data.tarieven.uurtarief, 0)}/uur per professional (min. {data.tarieven.min_uren} uur) via de SEPA-verzamelfactuur.
                      </div>
                    </div>
                  </div>
                );
              })()}
              <textarea
                value={alarmOmschrijving}
                onChange={(e) => setAlarmOmschrijving(e.target.value)}
                placeholder="Korte situatieschets (geen persoonsnamen of diagnoses)"
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-[11px] text-gray-500">
                Cascade: 0–60 s eigen regio → 61–120 s buurregio's → daarna landelijk met directe videolink. De eerste {alarmType === 'DUAL' ? 'twee specialisten die accepteren rukken' : 'specialist die accepteert rukt'} uit.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <Button variant="outline" onClick={() => setAlarmModal(null)}>
                Annuleren
              </Button>
              <Button variant="danger" loading={alarmMutation.isPending} onClick={() => alarmMutation.mutate({ locationId: alarmModal.location_id, type: alarmType, omschrijving: alarmOmschrijving })}>
                🚨 Alarm versturen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerCrisisPool;
